/**
 * Timing Section Component
 *
 * Card-level timing configuration (delay interval + type + day-of-delay).
 * Used by ALL automation actions identically.
 *
 * Handles both edit and readonly modes via readOnly prop.
 *
 * When delayType is 'week' or 'month', shows an additional dropdown
 * for selecting the specific day of the week or day of the month.
 */

import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { daysOfTheWeek } from 'src/constants';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { getOrdinalSuffix } from 'src/utils/utils';

import {
  getDelayTypesForTrigger,
  isTriggerBeforeType,
  isTriggerDelayable,
} from '../automationTriggers';
import { AutomationItem } from '../types';

interface TimingConfig {
  delayTypes?: ('day' | 'week' | 'month' | 'hour' | 'minute')[];
  allowBefore?: boolean;
  hideHourOfDay?: boolean;
  /** Delay follows the trigger phrase (no leading/trailing "after") */
  timingAfterTrigger?: boolean;
}

interface TimingSectionProps {
  automation: AutomationItem;
  index: number;
  config?: TimingConfig;
  readOnly: boolean;
  /** If true, shows "Every X days" instead of "X days after" (for recurring automations) */
  recurring?: boolean;
  /** Formik field name prefix (defaults to 'automations') */
  fieldPrefix?: string;
}

// Build month day options: 1st–28th + "end of the month"
const buildMonthDayOptions = (endOfMonthLabel: string) =>
  Array.from({ length: 28 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
  })).concat([{ value: '-1', label: endOfMonthLabel }]);

// Build week day options from the shared constant
const buildWeekDayOptions = () =>
  daysOfTheWeek.map((d) => ({
    value: d.value,
    label: d.label,
  }));

// Generate hour-of-day options (0-23)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour12 = i === 0 ? 12 : i > 12 ? i - 12 : i;
  const ampm = i < 12 ? 'AM' : 'PM';
  return { value: String(i), label: `${hour12}:00 ${ampm}` };
});

const formatHour = (hour: number | string | null | undefined): string | null => {
  if (hour == null) return null;
  const h = typeof hour === 'string' ? parseInt(hour, 10) : hour;
  if (isNaN(h)) return null;
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour12}:00 ${ampm}`;
};

/**
 * Format the dayOfDelay value for readonly display.
 * - Week: "on Sunday"
 * - Month (number): "on the 1st", "on the 15th", etc.
 * - Month (-1): "at the end of the month"
 */
const formatDayOfDelay = (
  delayType: string,
  dayOfDelay: number | string | null | undefined,
  t: (key: string) => string
): string => {
  if (dayOfDelay == null || dayOfDelay === '' || dayOfDelay === 0) return '';

  if (delayType === 'week') {
    return ` ${t('automations.timing.dayOfDelay.on')} ${dayOfDelay}`;
  }

  if (delayType === 'month') {
    const numVal = Number(dayOfDelay);
    if (numVal === -1) {
      return ` ${t('automations.timing.dayOfDelay.atEndOfMonth')}`;
    }
    if (!isNaN(numVal) && numVal > 0) {
      return ` ${t('automations.timing.dayOfDelay.onThe')} ${numVal}${getOrdinalSuffix(numVal)}`;
    }
  }

  return '';
};

export const TimingSection: FC<TimingSectionProps> = ({
  automation,
  index,
  config,
  readOnly,
  recurring = false,
  fieldPrefix = 'automations',
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const triggerKey = automation.reminderType || '';
  const delayInterval = automation.delayInterval ?? 1;
  const delayType = automation.delayType || 'day';
  const dayOfDelay = automation.dayOfDelay;
  const isBefore = !recurring && (isTriggerBeforeType(triggerKey) || config?.allowBefore);
  const timingAfterTrigger = config?.timingAfterTrigger ?? false;
  const delayable = recurring || isTriggerDelayable(triggerKey);

  const showDayOfDelay = delayType === 'week' || delayType === 'month';
  const showHourOfDay = recurring && delayType === 'day' && !config?.hideHourOfDay;

  // Get delay type options (from config or trigger)
  const delayTypeOptions = config?.delayTypes
    ? config.delayTypes.map((type) => ({
        value: type,
        label: t(`automations.timing.delayOption.${type}`),
      }))
    : getDelayTypesForTrigger(triggerKey).map((opt) => ({
        ...opt,
        label: t(`automations.timing.delayOption.${opt.value}`),
      }));

  // Day-of-delay options depending on delayType
  const dayOfDelayOptions =
    delayType === 'week'
      ? buildWeekDayOptions()
      : buildMonthDayOptions(t('automations.timing.dayOfDelay.endOfMonth'));

  // If delay is disabled for this trigger, hide timing entirely
  if (!delayable) {
    return null;
  }

  if (readOnly) {
    const dayOfDelayText = showDayOfDelay ? formatDayOfDelay(delayType, dayOfDelay, t) : '';

    // Recurring mode - "Every X days at 8:00 AM" / "Every X weeks on Monday"
    if (recurring) {
      const delayUnit = delayInterval === 1
        ? t(`automations.timing.delayUnit.${delayType}`)
        : t(`automations.timing.delayUnit.${delayType}_plural`);
      const hourText = showHourOfDay ? formatHour(automation.hour) : null;
      return (
        <span>
          {t('automations.timing.every')} {delayInterval} {delayUnit}
          {hourText && ` ${t('automations.timing.at')} ${hourText}`}
          {dayOfDelayText}
        </span>
      );
    }

    // Readonly mode - show summary text
    if (delayInterval === 0) {
      return <span>{t('automations.timing.immediately')}</span>;
    }

    const delayUnit = delayInterval === 1
      ? t(`automations.timing.delayUnit.${delayType}`)
      : t(`automations.timing.delayUnit.${delayType}_plural`);
    if (timingAfterTrigger) {
      return (
        <span>
          {delayInterval} {delayUnit}
          {dayOfDelayText}
        </span>
      );
    }
    const beforeText = isBefore
      ? t('automations.timing.before')
      : t('automations.timing.after');
    return (
      <span>
        {delayInterval} {delayUnit} {beforeText}
        {dayOfDelayText}
      </span>
    );
  }

  // Edit mode - show form fields
  return (
    <Stack spacing={1} direction="row" alignItems="center" flexWrap="wrap">
      {recurring && <Box>{t('automations.timing.every')}</Box>}
      <FormTextField
        name={`${fieldPrefix}.${index}.delayInterval`}
        type="number"
        sx={{ width: 80 }}
        InputProps={{ inputProps: { min: recurring ? '1' : '0' } }}
      />
      {delayTypeOptions.length > 0 && (
        <Box sx={{ minWidth: 120 }}>
          <FormSelect
            name={`${fieldPrefix}.${index}.delayType`}
            options={delayTypeOptions}
            onChange={() => {
              // Clear dayOfDelay when delay type changes (matches v2 behavior)
              setFieldValue(`${fieldPrefix}.${index}.dayOfDelay`, '');
            }}
          />
        </Box>
      )}
      {showHourOfDay && (
        <>
          <Box>{t('automations.timing.at')}</Box>
          <Box sx={{ minWidth: 130 }}>
            <FormSelect
              name={`${fieldPrefix}.${index}.hour`}
              options={HOUR_OPTIONS}
              prompt={t('automations.timing.selectTime')}
            />
          </Box>
        </>
      )}
      {showDayOfDelay && (
        <>
          <Box>
            {dayOfDelay == -1
              ? t('automations.timing.dayOfDelay.at')
              : t('automations.timing.dayOfDelay.on')}
            {delayType === 'month' && dayOfDelay
              ? ` ${t('automations.timing.dayOfDelay.the')}`
              : ''}
          </Box>
          <Box sx={{ minWidth: 140 }}>
            <FormSelect
              name={`${fieldPrefix}.${index}.dayOfDelay`}
              options={dayOfDelayOptions}
              prompt={t('automations.timing.dayOfDelay.anyDay')}
              MenuProps={{ sx: { '& .MuiList-root': { maxHeight: 300 } } }}
            />
          </Box>
        </>
      )}
      {!timingAfterTrigger && isBefore && <Box>{t('automations.timing.before')}</Box>}
      {!timingAfterTrigger &&
        !recurring &&
        !isBefore &&
        delayInterval > 0 && <Box>{t('automations.timing.after')}</Box>}
    </Stack>
  );
};
